import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Portal, Box, Typography, Button, Tabs, Tab,
  IconButton, CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import QRCode from "react-qr-code";
import CryptoJS from "crypto-js";
import { useTranslation } from "react-i18next";
import { formatSekAmount } from "../public/lib/format.jsx";
import { useBankIdSign } from "../hooks/useBankIdSign";
import { MODE_SAME_DEVICE, MODE_OTHER_DEVICE } from "../hooks/useBankId";
import { useBankIdReturn } from "../hooks/useBankIdReturn";
import {
  isMobileDevice, isIOSDevice, openBankIdApp, findPending, claimResume, releaseResume, claimReturnProblem,
} from "../utils/bankIdSession";
import { bankIdHints, AWAITING_RETURN_GRACE_MS } from "../utils/bankIdHints";
import { dsToast } from "../public/utils/dsToast";
import bankIdLogo from "../assets/logoBankId.png";
import ContractBankIdDialog from "../public/components/ds/ContractBankIdDialog";

function computeQrContent(qrStartToken, qrStartSecret, seconds) {
  const qrAuthCode = CryptoJS.HmacSHA256(
    seconds.toString(),
    qrStartSecret
  ).toString();
  return `bankid.${qrStartToken}.${seconds}.${qrAuthCode}`;
}

const BankIdSignModal = ({ open, onClose, onSuccess, presentation, signData }) => {
  const { t, i18n } = useTranslation();
  const HINTS = bankIdHints(i18n.language);

  // Structured, localized text shown in the signer's BankID app. Values are the
  // real deal data (reg, car, price, parties, agreement version); falls back to a
  // generic line when the deal data isn't available.
  const buildSignText = useCallback(() => {
    const sv = i18n.language?.startsWith("sv");
    const locale = sv ? "sv-SE" : "en-US";
    const reg = signData?.reg ? String(signData.reg).toUpperCase().trim() : null;
    if (!reg) return t("auth.bankId.signContractGeneric");

    const vehicleLine = [reg, (signData?.carName || "").trim()].filter(Boolean).join(" · ");

    const n = Number(signData?.price);
    const price = Number.isFinite(n) && n > 0
      ? formatSekAmount(n, i18n.language)
      : (signData?.price ? `${signData.price} kr` : "-");

    let version = "-";
    if (signData?.version) {
      const d = new Date(signData.version);
      if (!isNaN(d.getTime())) {
        version = d.toLocaleString(locale, {
          day: "numeric", month: "short", year: "numeric",
          hour: "2-digit", minute: "2-digit",
        });
      }
    }

    const L = t("auth.bankId.signLabels", { returnObjects: true }) || {};
    // One "Label: value" per line, no blank lines; footer directly after.
    const lines = [
      `${L.vehicle}: ${vehicleLine}`,
      `${L.price}: ${price}`,
      `${L.seller}: ${signData?.sellerName || "-"}`,
      `${L.buyer}: ${signData?.buyerName || "-"}`,
      `${L.version}: ${version}`,
      t("auth.bankId.signFooter"),
    ];
    return lines.join("\n");
  }, [signData, t, i18n.language]);

  const bankId = useBankIdSign();

  const isMobileBrowser = isMobileDevice();
  const [subTab, setSubTab] = useState(isMobileBrowser ? 1 : 0);
  const [step, setStep] = useState("idle");
  const [qrContent, setQrContent] = useState("");
  const [error, setError] = useState("");
  const [showAwaitingHelp, setShowAwaitingHelp] = useState(false);
  // A signing order this browser started that is being recovered after a reload or the BankID
  // app's return in a new tab. The dialog shows it even though the page has not opened it.
  const [recoveringRef, setRecoveringRef] = useState(null);
  const [returnTick, setReturnTick] = useState(0);
  const lastReturnRef = useRef(null);

  const visible = open || !!recoveringRef;

  const orderRefRef = useRef(null);
  const startTimeRef = useRef(null);
  const qrTimerRef = useRef(null);
  const launchWhenReadyRef = useRef(false);
  const launchedOrderRef = useRef(null);

  useEffect(() => {
    if (step === "polling" && bankId.qrStartToken && bankId.qrStartSecret) {
      startTimeRef.current = Date.now();
      const tick = () => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setQrContent(computeQrContent(bankId.qrStartToken, bankId.qrStartSecret, elapsed));
      };
      tick();
      qrTimerRef.current = setInterval(tick, 1000);
      return () => clearInterval(qrTimerRef.current);
    }
  }, [step, bankId.qrStartToken, bankId.qrStartSecret]);

  useEffect(() => {
    if (bankId.orderRef) orderRefRef.current = bankId.orderRef;
  }, [bankId.orderRef]);

  const callbacks = useCallback(() => ({
    onComplete: (pn, _name, orderRef) => {
      clearInterval(qrTimerRef.current);
      setStep("idle");
      setRecoveringRef(null);
      // The page's own completion (DealCompletedBy*) runs for a recovered order too; it is
      // idempotent with the server, which already recorded the signature in CollectSign.
      onSuccess(pn, orderRef);
    },
    onFailed: (hint) => {
      clearInterval(qrTimerRef.current);
      setStep("idle");
      setError(HINTS[hint] ?? t("auth.bankId.failed"));
    },
  }), [onSuccess, HINTS, t]);

  const startOrder = useCallback((tab, { launch }) => {
    setError("");
    setShowAwaitingHelp(false);
    setStep("polling");
    startTimeRef.current = Date.now();
    setQrContent("");
    // Never on iPhone/iPad: iOS opens the app only from the user's own tap (the button below), and
    // a launch after the order returns lands on BankID's "couldn't be launched" page.
    launchWhenReadyRef.current = launch && tab === 1 && !isIOSDevice();

    bankId.start({
      mode: tab === 1 ? MODE_SAME_DEVICE : MODE_OTHER_DEVICE,
      userVisibleData: buildSignText(),
      contractId: signData?.contractId,
      role: signData?.role,
      lang: i18n.language?.startsWith("sv") ? "sv" : "en",
      ...callbacks(),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankId.start, buildSignText, callbacks, signData?.contractId, signData?.role, i18n.language]);

  // The user tapped "sign" to get here: on a phone that is the choice to sign on this device,
  // so the app opens as soon as the order exists.
  useEffect(() => {
    if (open && step === "idle" && !recoveringRef) {
      const tab = isMobileBrowser ? 1 : 0;
      setSubTab(tab);
      startOrder(tab, { launch: isMobileBrowser });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Recovery: this browser started a signature for this contract/role ──────
  useBankIdReturn((result) => {
    lastReturnRef.current = result;
    setReturnTick((n) => n + 1);
  });
  useEffect(() => {
    if (open || recoveringRef || !signData?.contractId || !signData?.role) return;
    const pending = findPending({ purpose: "sign", contractId: signData.contractId, role: signData.role });
    if (pending && claimResume(pending.orderRef)) {
      setRecoveringRef(pending.orderRef);
      setSubTab(1);
      setError("");
      setStep("polling");
      launchWhenReadyRef.current = false;     // never relaunch a recovered order
      bankId.resume(pending.orderRef, callbacks());
      return;
    }
    const result = lastReturnRef.current;
    if (claimReturnProblem(result)) {
      dsToast("error", { title: "BankID", text: HINTS[result.status] ?? HINTS.unknownOrder }, { autoClose: 12000 });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnTick, signData?.contractId, signData?.role]);

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (visible) {
      wasOpenRef.current = true;
    } else if (wasOpenRef.current) {
      // A finished order is no longer active in the hook, so this never cancels a success.
      clearInterval(qrTimerRef.current);
      bankId.cancel();
      launchWhenReadyRef.current = false;
      launchedOrderRef.current = null;
      setStep("idle");
      setQrContent("");
      setError("");
      setShowAwaitingHelp(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleCancel = useCallback(async () => {
    clearInterval(qrTimerRef.current);
    await bankId.cancel();
    if (recoveringRef) releaseResume(recoveringRef);
    setRecoveringRef(null);
    setStep("idle");
    setError("");
    onClose();
  }, [bankId, onClose, recoveringRef]);

  // Each tab runs its own order (QR orders carry no autoStartToken, same-device orders no QR).
  const handleTabSelect = useCallback((tab) => {
    if (tab === subTab) return;
    setSubTab(tab);
    clearInterval(qrTimerRef.current);
    bankId.cancel();
    if (recoveringRef) releaseResume(recoveringRef);
    setRecoveringRef(null);
    startOrder(tab, { launch: tab === 1 });
  }, [bankId, recoveringRef, startOrder, subTab]);

  // BankID's documented launch (app.bankid.com on mobile, bankid:/// on desktop), without the
  // deprecated "redirect" parameter: the server gave BankID the returnUrl for this order, which
  // brings the user back to this contract page. Completion is still only ever read from
  // CollectSign — the return itself proves nothing.
  const handleOpenOnDevice = useCallback(() => {
    if (bankId.autoStartToken) {
      launchedOrderRef.current = bankId.orderRef;
      openBankIdApp(bankId.autoStartToken);
    } else if (step === "idle") {
      startOrder(1, { launch: true });
    }
  }, [bankId.autoStartToken, bankId.orderRef, startOrder, step]);

  useEffect(() => {
    if (!visible || subTab !== 1 || step !== "polling" || !bankId.autoStartToken) return;
    if (!launchWhenReadyRef.current || launchedOrderRef.current === bankId.orderRef) return;
    launchWhenReadyRef.current = false;
    launchedOrderRef.current = bankId.orderRef;
    openBankIdApp(bankId.autoStartToken);
  }, [visible, subTab, step, bankId.autoStartToken, bankId.orderRef]);

  useEffect(() => {
    if (bankId.hintCode !== "awaitingReturn") {
      setShowAwaitingHelp(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setShowAwaitingHelp(true), AWAITING_RETURN_GRACE_MS);
    return () => window.clearTimeout(timer);
  }, [bankId.hintCode]);

  const isSv = i18n.language?.startsWith("sv");
  const title = isSv ? "Skriv under avtal" : "Sign Contract";
  const subtitle = isSv
    ? "Skriv under med BankID för att bekräfta ditt avtal"
    : "Sign with BankID to confirm your deal";

  const hintText = bankId.hintCode === "awaitingReturn"
    ? (showAwaitingHelp ? HINTS.awaitingReturn : t("auth.bankId.waiting"))
    : recoveringRef && !bankId.hintCode
      ? t("auth.bankId.resuming")
      : bankId.hintCode
        ? (HINTS[bankId.hintCode] ?? bankId.hintCode)
        : (subTab === 0 ? t("auth.bankId.scanQrHint") : t("auth.bankId.openAppHint"));

  if (presentation === "contract") {
    return <ContractBankIdDialog open={visible} title={title} subtitle={subtitle} t={t} subTab={subTab} setSubTab={handleTabSelect} step={step} qrContent={qrContent} hintText={hintText} error={error} autoStartToken={bankId.autoStartToken} onCancel={handleCancel} onOpenOnDevice={handleOpenOnDevice} onRetry={() => startOrder(0, { launch: false })} />;
  }

  return (
    <Portal>
    {visible && <Box sx={{
      position: "fixed", inset: 0, zIndex: 9999,
      overflow: "auto",
      background: "linear-gradient(160deg, #f0fdf4 0%, #dcfce7 40%, #ecfdf5 100%)",
    }}>
      <IconButton
        onClick={handleCancel}
        size="small"
        sx={{
          position: "fixed", top: 14, right: 14, zIndex: 10,
          backgroundColor: "rgba(255,255,255,0.9)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          "&:hover": { backgroundColor: "#fff" },
        }}>
        <CloseIcon fontSize="small" />
      </IconButton>

      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minHeight: "100vh", p: 2, pt: 5 }}>

        <Box sx={{ mb: 3 }}>
          <img src={bankIdLogo} alt="BankID" style={{ height: 48, objectFit: "contain" }} />
        </Box>

        <Box sx={{ width: "100%", maxWidth: 480, backgroundColor: "#fff", borderRadius: 4, p: { xs: 3, sm: 4 }, boxShadow: "0 8px 40px rgba(0,0,0,0.10)", border: "1px solid rgba(16,185,129,0.1)" }}>

          <Typography variant="h5" fontWeight="bold" textAlign="center" mb={0.5}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
            {subtitle}
          </Typography>

          <Box sx={{ backgroundColor: "#f1f5f9", borderRadius: 3, p: 0.5, mb: 3 }}>
            <Tabs
              value={subTab}
              onChange={(_, v) => handleTabSelect(v)}
              variant="fullWidth"
              TabIndicatorProps={{ style: { display: "none" } }}
              textColor="inherit"
              sx={{ minHeight: "unset" }}
            >
              <Tab label={t("auth.bankId.scanQr")}
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2.5, py: 0.8, minHeight: "unset",
                  backgroundColor: subTab === 0 ? "#fff" : "transparent",
                  color: subTab === 0 ? "#000" : "#64748b",
                  boxShadow: subTab === 0 ? "0 2px 6px rgba(0,0,0,0.1)" : "none" }} />
              <Tab label={t("auth.bankId.thisDevice")}
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2.5, py: 0.8, minHeight: "unset",
                  backgroundColor: subTab === 1 ? "#fff" : "transparent",
                  color: subTab === 1 ? "#000" : "#64748b",
                  boxShadow: subTab === 1 ? "0 2px 6px rgba(0,0,0,0.1)" : "none" }} />
            </Tabs>
          </Box>

          {subTab === 0 && (
            <Box textAlign="center">
              {step === "polling" && qrContent ? (
                <>
                  <Box sx={{ display: "inline-block", p: 2.5, border: "2px solid #e2e8f0", borderRadius: 3, mb: 2, backgroundColor: "#fff" }}>
                    <QRCode value={qrContent} size={220} />
                  </Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {t("auth.bankId.scanQrInstruction")}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#10b981", fontWeight: 600 }}>
                    {hintText}
                  </Typography>
                </>
              ) : (
                <Box sx={{ height: 260, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CircularProgress sx={{ color: "#10b981" }} />
                </Box>
              )}
              {error && <Typography color="error" variant="body2" mt={2}>{error}</Typography>}
              <Box mt={3}>
                <Button variant="outlined" onClick={handleCancel}
                  sx={{ borderColor: "#e2e8f0", color: "#64748b", borderRadius: 3, px: 5, "&:hover": { borderColor: "#cbd5e1" } }}>
                  {t("auth.bankId.cancel")}
                </Button>
              </Box>
            </Box>
          )}

          {subTab === 1 && (
            <Box textAlign="center">
              <Box sx={{ mb: 3, p: 3, backgroundColor: "#f8fafc", borderRadius: 3, border: "1px solid #e2e8f0" }}>
                <Typography variant="body2" color="text.secondary" mb={step === "polling" ? 1.5 : 0}>
                  {t("auth.bankId.openAppHint")}
                </Typography>
                {step === "polling" && (
                  <Typography variant="body2" sx={{ color: "#10b981", fontWeight: 600 }}>
                    {hintText}
                  </Typography>
                )}
              </Box>

              {error && <Typography color="error" variant="body2" mb={2}>{error}</Typography>}

              <Button fullWidth variant="contained" onClick={handleOpenOnDevice}
                disabled={step === "polling" && !bankId.autoStartToken}
                sx={{ backgroundColor: "#193E8B", color: "#fff", fontWeight: 700, borderRadius: 3, py: 1.5, mb: 2,
                  "&:hover": { backgroundColor: "#142d63" }, "&:disabled": { backgroundColor: "#93c5fd" } }}>
                {step === "polling" && !bankId.autoStartToken
                  ? <><CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />{t("auth.bankId.waiting")}</>
                  : t("auth.bankId.openBankIdApp")}
              </Button>

              <Button variant="outlined" onClick={handleCancel}
                sx={{ borderColor: "#e2e8f0", color: "#64748b", borderRadius: 3, px: 5, "&:hover": { borderColor: "#cbd5e1" } }}>
                {t("auth.bankId.cancel")}
              </Button>
            </Box>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" textAlign="center" mt={3} sx={{ maxWidth: 400, px: 2 }}>
          {t("auth.agreementTextStart")}{" "}
          <span style={{ color: "#10b981", cursor: "pointer" }}>{t("auth.termsOfService")}</span>{" "}
          {t("auth.agreementTextMiddle")}{" "}
          <span style={{ color: "#10b981", cursor: "pointer" }}>{t("auth.privacyPolicy")}</span>.
        </Typography>
      </Box>
    </Box>}
    </Portal>
  );
};

export default BankIdSignModal;
